import * as THREE from 'three';
import { Layer } from '../store/useAppStore';

// ── Layer transform ─────────────────────────────────────────────────────────

// Scratch objects reused per buildLayerTransform call — avoids per-frame GC pressure.
const _btM4a    = new THREE.Matrix4();
const _btM4b    = new THREE.Matrix4();
const _btQ      = new THREE.Quaternion();
const _btFrom   = new THREE.Vector3(1, 0, 0); // 'from' vector is always (1,0,0)
const _btTarget = new THREE.Vector3();
const _btEuler  = new THREE.Euler();

/**
 * Build the forward transform matrix for a layer: world-space UV → layer-local UV.
 * translateX is encoded as a Y-axis sphere rotation (longitude shift).
 * translateY is handled via UV v-shift in the composite shader (not here).
 * Display matrix (world → layer-local): (R_user × R_translate)⁻¹ = transpose
 */
export function buildLayerTransform(layer: Layer, dest?: THREE.Matrix4): THREE.Matrix4 {
  const tu = (((0.5 + layer.translate.x) % 1) + 1) % 1;
  const theta = (1 - tu) * Math.PI * 2 + Math.PI;
  _btTarget.set(Math.cos(theta), 0, Math.sin(theta));
  _btQ.setFromUnitVectors(_btFrom, _btTarget); // equatorial target is always unit-length
  _btM4a.makeRotationFromQuaternion(_btQ);     // R_translate
  _btEuler.set(
    THREE.MathUtils.degToRad(layer.rotate.x),
    THREE.MathUtils.degToRad(layer.rotate.y),
    THREE.MathUtils.degToRad(layer.rotate.z),
    'XYZ',
  );
  _btM4b.makeRotationFromEuler(_btEuler);      // R_user
  return (dest ?? new THREE.Matrix4()).multiplyMatrices(_btM4b, _btM4a);
}

// ── Layer hierarchy helpers ─────────────────────────────────────────────────

/** Returns true only if the layer and every ancestor are visible. */
export function isLayerEffectivelyVisible(layer: Layer, allLayers: Layer[]): boolean {
  if (!layer.visible) return false;
  if (!layer.parentId) return true;
  const parent = allLayers.find((l) => l.id === layer.parentId);
  if (!parent) return true;
  return isLayerEffectivelyVisible(parent, allLayers);
}

/** Returns true if the layer or any ancestor is locked. */
export function isEffectivelyLocked(layer: Layer, allLayers: Layer[]): boolean {
  if (layer.locked) return true;
  if (!layer.parentId) return false;
  const parent = allLayers.find((l) => l.id === layer.parentId);
  if (!parent) return false;
  return isEffectivelyLocked(parent, allLayers);
}

/** Product of opacities along the full parent chain (inclusive of the layer itself). */
export function getEffectiveOpacity(layer: Layer, allLayers: Layer[]): number {
  let op = layer.opacity;
  let current: Layer = layer;
  while (current.parentId) {
    const parent = allLayers.find((l) => l.id === current.parentId);
    if (!parent) break;
    op *= parent.opacity;
    current = parent;
  }
  return op;
}

/** Sum of translateY values along the full parent chain. */
export function getEffectiveTranslateY(layer: Layer, allLayers: Layer[]): number {
  let ty = layer.translate.y;
  let current: Layer = layer;
  while (current.parentId) {
    const parent = allLayers.find((l) => l.id === current.parentId);
    if (!parent) break;
    ty += parent.translate.y;
    current = parent;
  }
  return ty;
}

/**
 * Builds the effective rotation+translation matrix by multiplying parent
 * transforms onto the layer's own.  The parent transform is applied AFTER
 * the layer's own (i.e. parentM × ownM), so parent rotation offsets all
 * child content as an additive world-space rotation.
 */
export function buildEffectiveLayerTransform(layer: Layer, allLayers: Layer[]): THREE.Matrix4 {
  const own = buildLayerTransform(layer);
  if (!layer.parentId) return own;
  const parent = allLayers.find((l) => l.id === layer.parentId);
  if (!parent) return own;
  const parentM = buildEffectiveLayerTransform(parent, allLayers);
  return new THREE.Matrix4().multiplyMatrices(parentM, own);
}

/** Returns the nesting depth of a layer in the parent chain (0 = root). */
export function getLayerDepth(layerId: string, allLayers: Layer[]): number {
  const layer = allLayers.find((l) => l.id === layerId);
  if (!layer?.parentId) return 0;
  if (!allLayers.some((l) => l.id === layer.parentId)) return 0;
  return 1 + getLayerDepth(layer.parentId, allLayers);
}

// Pre-allocated pool for buildLayerEffectCache — reused across calls to eliminate
// per-flatten Matrix4 allocations.  Grows on demand; never shrinks.
const _cachePool: THREE.Matrix4[] = [];
let   _cachePoolIdx                = 0;
// Single scratch for each layer's own transform within the resolve closure.
// Safe to share because parent is resolved BEFORE own is computed.
const _cacheOwnScratch = new THREE.Matrix4();

/** Pre-computed per-layer data that would otherwise require repeated parent-chain walks. */
export interface EffectiveLayerData {
  visible: boolean;
  opacity: number;
  translateY: number;
  matrix: THREE.Matrix4;
}

/**
 * Builds effective transform data for every layer in a single O(N) pass.
 * Each parent chain is resolved exactly once (memoised via the returned Map),
 * so loops that need visible/opacity/translateY/matrix for every layer pay
 * O(N) total instead of O(N × depth) with repeated allLayers.find() calls.
 */
export function buildLayerEffectCache(layers: Layer[]): Map<string, EffectiveLayerData> {
  const byId = new Map<string, Layer>(layers.map((l) => [l.id, l]));
  const cache = new Map<string, EffectiveLayerData>();
  _cachePoolIdx = 0;

  const resolve = (layer: Layer): EffectiveLayerData => {
    const hit = cache.get(layer.id);
    if (hit) return hit;

    // Resolve parent FIRST so _cacheOwnScratch is free when we compute 'own'.
    const parent = layer.parentId ? byId.get(layer.parentId) : undefined;
    const p = parent ? resolve(parent) : null;

    // Compute this layer's own transform into the scratch — safe now that the
    // recursive call has finished and no longer needs the scratch.
    const own = buildLayerTransform(layer, _cacheOwnScratch);

    // Write the accumulated result into a pooled matrix (no allocation).
    if (!_cachePool[_cachePoolIdx]) _cachePool[_cachePoolIdx] = new THREE.Matrix4();
    const mat = _cachePool[_cachePoolIdx++];

    if (!p) {
      mat.copy(own);
      const data: EffectiveLayerData = {
        visible:    layer.visible,
        opacity:    layer.opacity,
        translateY: layer.translate.y,
        matrix:     mat,
      };
      cache.set(layer.id, data);
      return data;
    }

    mat.multiplyMatrices(p.matrix, own);
    const data: EffectiveLayerData = {
      visible:    layer.visible && p.visible,
      opacity:    layer.opacity * p.opacity,
      translateY: layer.translate.y + p.translateY,
      matrix:     mat,
    };
    cache.set(layer.id, data);
    return data;
  };

  for (const layer of layers) resolve(layer);
  return cache;
}

// ── Spline helpers ──────────────────────────────────────────────────────────

export function catmullRomPoint(
  p0: THREE.Vector3,
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3,
  t: number,
): THREE.Vector3 {
  const t2 = t * t;
  const t3 = t2 * t;
  const a = p1.clone().multiplyScalar(2);
  const b = p2.clone().sub(p0);
  const c = p0.clone().multiplyScalar(2)
    .sub(p1.clone().multiplyScalar(5))
    .add(p2.clone().multiplyScalar(4))
    .sub(p3);
  const d = p3.clone().sub(p0)
    .add(p1.clone().multiplyScalar(3))
    .sub(p2.clone().multiplyScalar(3));
  return a
    .add(b.multiplyScalar(t))
    .add(c.multiplyScalar(t2))
    .add(d.multiplyScalar(t3))
    .multiplyScalar(0.5);
}

export function getSplinePoints(
  p0: THREE.Vector3,
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3,
  steps: number,
  smooth: number,
): THREE.Vector3[] {
  const result: THREE.Vector3[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const linear = p1.clone().lerp(p2, t);
    const cr = catmullRomPoint(p0, p1, p2, p3, t);
    result.push(linear.lerp(cr, smooth));
  }
  return result;
}

// ── Stroke resampling ───────────────────────────────────────────────────────

/**
 * Resample raw stroke hit-points along a Catmull-Rom spline at a
 * uniform arc-length step, returning up to 1 024 densely-packed points.
 */
export function resampleStrokeSpine(
  rawPts: { point: THREE.Vector3; pressure: number }[],
  targetStep: number,
): { point: THREE.Vector3; pressure: number }[] {
  const n = rawPts.length;
  if (n < 2) return rawPts.map((p) => ({ point: p.point.clone(), pressure: p.pressure }));
  const curve = new THREE.CatmullRomCurve3(
    rawPts.map((p) => p.point.clone()), false, 'catmullrom', 0.5,
  );
  const arcLen = curve.getLength();
  const numSamples = Math.min(1024, Math.max(2, Math.ceil(arcLen / targetStep) + 1));
  const pts3D = curve.getSpacedPoints(numSamples - 1);
  const rawArcLens: number[] = [0];
  for (let i = 1; i < n; i++)
    rawArcLens.push(rawArcLens[i - 1] + rawPts[i - 1].point.distanceTo(rawPts[i].point));
  const totalRaw = rawArcLens[n - 1];
  return pts3D.map((pt, idx) => {
    const frac = numSamples > 1 ? idx / (numSamples - 1) : 0;
    const rawPos = frac * totalRaw;
    let seg = 0;
    while (seg < n - 2 && rawArcLens[seg + 1] < rawPos) seg++;
    const span = rawArcLens[seg + 1] - rawArcLens[seg];
    const t = span > 0 ? (rawPos - rawArcLens[seg]) / span : 0;
    const pressure =
      rawPts[seg].pressure +
      (rawPts[seg + 1].pressure - rawPts[seg].pressure) * Math.min(1, Math.max(0, t));
    return { point: pt, pressure };
  });
}
